import {
  type LoadableState,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { useCallback, useRef, useState } from 'react';
import type {
  AutoSelectConfirm,
  AutoSelectItem,
  CustomizeRequest,
  CustomizeResponse,
  PendingTrigger
} from '~/contracts/new-cars';
import {
  buildAutoSelectItem,
  buildUnavailableFinalState,
  computeStrictRemovals,
  countSelectedCategories,
  detectAutoSelectsFromResponse,
  detectConflicts,
  detectUnavailableRemovals,
  hasPicks,
  isPendingTrigger
} from './autoSelectUtils';
import detectTrimLevelSelections from './detectTrimLevelSelections';
import { NoMatchingVehiclesError } from './services/useGetCustomizeOptions';

type AutoSelectArgs = {
  acceptPending: (finalState: CustomizeRequest) => void;
  clearPending: () => void;
  customizeOptions: LoadableState<CustomizeResponse>;
  pendingCustomizeOptions: LoadableState<CustomizeResponse>;
  pendingSelectedOptions: CustomizeRequest | null;
  pendingTrigger: PendingTrigger | null;
  resetSelectedOptions: () => void;
  selectedOptions: CustomizeRequest | null;
  setSelectedOptions: (value: CustomizeRequest | null) => void;
};

export default function useAutoSelect({
  acceptPending,
  clearPending,
  customizeOptions,
  pendingCustomizeOptions,
  pendingSelectedOptions,
  pendingTrigger,
  resetSelectedOptions,
  selectedOptions,
  setSelectedOptions
}: AutoSelectArgs) {
  const [confirm, setConfirm] = useState<AutoSelectConfirm | null>(null);
  const [trimLevelSelections, setTrimLevelSelections] = useState<
    Partial<CustomizeRequest>
  >({});

  const customizeOptionsRef = useRef(customizeOptions);
  customizeOptionsRef.current = customizeOptions;

  const selectedOptionsRef = useRef(selectedOptions);
  selectedOptionsRef.current = selectedOptions;

  const pendingTriggerRef = useRef(pendingTrigger);
  pendingTriggerRef.current = pendingTrigger;

  const pendingSelectedOptionsRef = useRef(pendingSelectedOptions);
  pendingSelectedOptionsRef.current = pendingSelectedOptions;

  const preCommitSelectedOptionsRef = useRef<CustomizeRequest | null>(null);
  const preCommitTriggerRef = useRef<AutoSelectItem | null>(null);
  const trimSelectionsAppliedRef = useRef(false);

  useSubsequentEffect(() => {
    if (!customizeOptions.isLoaded) return;

    preCommitTriggerRef.current = null;

    const sel = selectedOptionsRef.current;

    if (hasPicks(sel)) return;

    if (!trimSelectionsAppliedRef.current && sel) {
      trimSelectionsAppliedRef.current = true;
      const trimSelections = detectTrimLevelSelections(customizeOptions.data);
      setTrimLevelSelections(trimSelections);

      if (Object.keys(trimSelections).length > 0) {
        setSelectedOptions({ ...sel, ...trimSelections });
      }
    }
  }, [customizeOptions.data, customizeOptions.isLoaded, setSelectedOptions]);

  useSubsequentEffect(() => {
    if (customizeOptions.error instanceof NoMatchingVehiclesError) {
      setConfirm({
        additions: [],
        confirmType: 'reset',
        pendingFinalState: null,
        removals: [],
        trigger: preCommitTriggerRef.current
      });
      preCommitTriggerRef.current = null;
    }
  }, [customizeOptions.error]);

  useSubsequentEffect(() => {
    const trigger = pendingTriggerRef.current;
    const pending = pendingSelectedOptionsRef.current;

    if (!pending || !trigger) return;

    if (pendingCustomizeOptions.hasError) {
      if (pendingCustomizeOptions.error instanceof NoMatchingVehiclesError) {
        setConfirm({
          additions: [],
          confirmType: 'reset',
          pendingFinalState: null,
          removals: [],
          trigger: buildAutoSelectItem(
            trigger.key,
            trigger.code,
            customizeOptionsRef.current.data
          )
        });
      }

      clearPending();

      return;
    }

    if (!pendingCustomizeOptions.isLoaded) return;

    const sel = selectedOptionsRef.current;

    if (trigger.type === 'deselect') {
      const removals = detectUnavailableRemovals(
        sel ?? pending,
        pendingCustomizeOptions.data
      ).filter(r => r.key !== trigger.key || r.code !== trigger.code);

      if (removals.length === 0) {
        acceptPending(pending);

        return;
      }

      setConfirm({
        additions: [],
        confirmType: 'deselect',
        pendingFinalState: pending,
        removals,
        trigger: buildAutoSelectItem(
          trigger.key,
          trigger.code,
          customizeOptionsRef.current.data
        )
      });

      return;
    }

    if (trigger.type === 'unavailable') {
      const triggerItem = buildAutoSelectItem(
        trigger.key,
        trigger.code,
        pendingCustomizeOptions.data
      );
      const result = detectAutoSelectsFromResponse(
        pendingCustomizeOptions.data,
        pending,
        null,
        triggerItem
      );

      const additions = (result?.items ?? []).filter(i => {
        if (isPendingTrigger(i, pending)) return false;

        switch (i.key) {
          case 'driveType':
            return sel?.driveType !== i.code;
          case 'engine':
            return sel?.engineCode !== i.code;
          case 'exterior':
            return sel?.exteriorColorCode !== i.code;
          case 'interior':
            return sel?.interiorColorCode !== i.code;
          case 'package':
            return !(sel?.packageOptionCodes ?? []).includes(i.code);
          default:
            return true;
        }
      });

      const selectedCategoryCount = countSelectedCategories(sel);

      // When three or more categories are selected and the selected item is
      // unavailable, we have a high likelihood of conflicts. Therefore, we tell
      // the user that all selections will be removed, except the one they just
      // made and its auto-selects. It is a mild annoyance that prevents us from
      // producing irresolvable conflicts and a poor user experience.
      if (selectedCategoryCount >= 3) {
        const newFinalState = { ...pending, ...(result?.updates ?? {}) };
        const removals = sel
          ? computeStrictRemovals(
              sel,
              newFinalState,
              trigger.key,
              customizeOptionsRef.current.data
            )
          : [];

        if (additions.length === 0 && removals.length === 0) {
          preCommitSelectedOptionsRef.current = sel;
          preCommitTriggerRef.current = triggerItem;
          setSelectedOptions(newFinalState);
          clearPending();

          return;
        }

        setConfirm({
          additions,
          confirmType: 'unavailable',
          pendingFinalState: newFinalState,
          removals,
          trigger: triggerItem
        });

        return;
      }

      const packageRemovals: AutoSelectItem[] =
        trigger.key === 'package'
          ? (sel?.packageOptionCodes ?? [])
              .filter(code => code !== trigger.code)
              .map(code =>
                buildAutoSelectItem(
                  'package',
                  code,
                  customizeOptionsRef.current.data
                )
              )
          : [];

      const responseRemovals = detectUnavailableRemovals(
        sel ?? pending,
        pendingCustomizeOptions.data
      ).filter(
        r =>
          r.key !== trigger.key ||
          (r.key === 'package' && r.code !== trigger.code)
      );

      const removals =
        trigger.key === 'package'
          ? [
              ...packageRemovals,
              ...responseRemovals.filter(r => r.key !== 'package')
            ]
          : responseRemovals;

      let newFinalState = buildUnavailableFinalState(
        pending,
        sel ?? pending,
        pendingCustomizeOptions.data,
        result?.updates ?? {}
      );

      if (trigger.key === 'package') {
        const existingCodes = new Set(sel?.packageOptionCodes ?? []);
        const allowedPackages = new Set([
          ...(pending.packageOptionCodes ?? []),
          ...(result?.updates?.packageOptionCodes ?? []).filter(
            c => !existingCodes.has(c)
          )
        ]);

        newFinalState = {
          ...newFinalState,
          packageOptionCodes: [...allowedPackages]
        };
      }

      if (additions.length === 0 && removals.length === 0) {
        preCommitSelectedOptionsRef.current = sel;
        preCommitTriggerRef.current = triggerItem;
        setSelectedOptions(newFinalState);
        clearPending();

        return;
      }

      setConfirm({
        additions,
        confirmType: 'unavailable',
        pendingFinalState: newFinalState,
        removals,
        trigger: triggerItem
      });

      return;
    }

    // type === 'select'
    const result = detectAutoSelectsFromResponse(
      pendingCustomizeOptions.data,
      pending,
      sel
    );

    if (!result) {
      acceptPending(pending);

      return;
    }

    const newFinalState = { ...pending, ...result.updates };
    const additions = result.items.filter(i => !isPendingTrigger(i, pending));
    const conflicts = sel ? detectConflicts(newFinalState, sel, pending) : [];

    if (additions.length === 0 && conflicts.length === 0) {
      acceptPending(newFinalState);

      return;
    }

    setConfirm({
      additions,
      confirmType: 'select',
      pendingFinalState: newFinalState,
      removals: conflicts,
      trigger: result.trigger
    });
  }, [
    acceptPending,
    clearPending,
    pendingCustomizeOptions.data,
    pendingCustomizeOptions.error,
    pendingCustomizeOptions.hasError,
    pendingCustomizeOptions.isLoaded,
    setSelectedOptions
  ]);

  const handleContinue = useCallback(() => {
    if (!confirm) return;

    if (confirm.confirmType === 'reset') {
      preCommitSelectedOptionsRef.current = null;
      trimSelectionsAppliedRef.current = false;
      setTrimLevelSelections({});
      resetSelectedOptions();
    } else if (confirm.pendingFinalState != null) {
      acceptPending(confirm.pendingFinalState);
    }

    clearPending();
    setConfirm(null);
  }, [acceptPending, clearPending, confirm, resetSelectedOptions]);

  const handleCancel = useCallback(() => {
    if (
      confirm?.confirmType === 'reset' &&
      preCommitSelectedOptionsRef.current
    ) {
      setSelectedOptions(preCommitSelectedOptionsRef.current);
      preCommitSelectedOptionsRef.current = null;
    }

    clearPending();
    setConfirm(null);
  }, [clearPending, confirm, setSelectedOptions]);

  return { confirm, handleCancel, handleContinue, trimLevelSelections };
}
